import {
  type FormEvent,
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  ArrowDown,
  ArrowUp,
  Archive,
  Eye,
  EyeClosed,
  Pencil,
  RefreshCw,
  Save,
  Search,
  Trash2,
  Upload,
  Plus,
} from 'lucide-react'
import {
  type AdminAnswer,
  type AdminApi,
  type AdminMedia,
  type AdminApiOptions,
  type AdminStation,
  type AdminStationSummary,
  type CreateSubquestionInput,
  type UpdateAnswerInput,
  type UpdateStationInput,
  type UpdateSubquestionInput,
  type CreateStationInput,
  buildStationDraftPayload,
  createAdminApi as createAdminApiClient,
  formatStatusLabel,
  normalizeStationAnomaly,
  parseTagsFromInput,
} from './adminApi'
import { getFirebaseIdToken, useAuthSession } from '../../lib/firebase'
import { type StationStatus, type StationType, type Section } from '../../lib/types'
import { AdminApiError } from './adminApi'
import './admin.css'

export interface AdminCMSPageProps {
  getAuthToken?: AdminApiOptions['getToken']
}

const STATION_TYPES: Array<StationType> = ['clinical', 'image', 'instrument', 'mixed']
const STATION_TYPE_LABELS: Record<StationType, string> = {
  clinical: 'حالة سريرية',
  image: 'صور',
  instrument: 'أداة جراحية',
  mixed: 'محتوى متنوع',
}
const DEFAULT_STATUS: StationStatus = 'draft'

interface DraftMedia {
  id: string
  key: string
  url: string
  altText: string
  filename: string | null
  sortOrder: number
}

interface DraftAnswer {
  id?: string
  localId: string
  body: string
  isPrimary: boolean
  sortOrder: number
}

interface DraftSubquestion {
  id?: string
  localId: string
  prompt: string
  questionType: string
  sortOrder: number
  marks: string
  answers: DraftAnswer[]
}

interface StationDraft {
  id?: string
  sectionId: string
  title: string
  scenario: string
  type: StationType
  status: StationStatus
  sortOrder: number
  source: string
  sourcePages: string
  tags: string
  images: DraftMedia[]
  subquestions: DraftSubquestion[]
}

type EditorMode = 'idle' | 'create' | 'edit'

interface StationListState {
  total: number
  page: number
  pageSize: number
  hasMore: boolean
}

function toLocalId(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2)}-${Date.now()}`
}

function mapMediaToDraft(media: AdminMedia): DraftMedia {
  return {
    id: media.id,
    key: media.key,
    url: media.url ?? media.key,
    altText: media.altText ?? '',
    filename: media.filename,
    sortOrder: media.sortOrder,
  }
}

function mapAnswer(answer: AdminAnswer): DraftAnswer {
  return {
    id: answer.id,
    localId: toLocalId(`answer-${answer.id}`),
    body: answer.body,
    isPrimary: answer.isPrimary,
    sortOrder: answer.sortOrder || answer.order || 1,
  }
}

function mapSubquestion(stationSubquestion: AdminStation['subquestions'][number]): DraftSubquestion {
  return {
    id: stationSubquestion.id,
    localId: toLocalId(`sub-${stationSubquestion.id}`),
    prompt: stationSubquestion.prompt,
    questionType: stationSubquestion.questionType || 'text',
    sortOrder: stationSubquestion.sortOrder || stationSubquestion.order || 1,
    marks: stationSubquestion.marks === null || stationSubquestion.marks === undefined
      ? ''
      : String(stationSubquestion.marks),
    answers: stationSubquestion.answers.map(mapAnswer),
  }
}

function buildEmptyAnswer(): DraftAnswer {
  return {
    localId: toLocalId('answer'),
    body: '',
    isPrimary: false,
    sortOrder: 1,
  }
}

function buildEmptySubquestion(sortOrder = 1): DraftSubquestion {
  return {
    localId: toLocalId('subquestion'),
    prompt: '',
    questionType: 'text',
    sortOrder,
    marks: '',
    answers: [buildEmptyAnswer()],
  }
}

function createBlankDraft(sections: Section[], stations: AdminStationSummary[]): StationDraft {
  const nextOrder = stations.length
    ? Math.max(...stations.map((station) => station.sortOrder)) + 1
    : 1
  const firstSection = sections[0]?.id ?? ''
  return {
    sectionId: firstSection,
    title: '',
    scenario: '',
    type: 'clinical',
    status: DEFAULT_STATUS,
    sortOrder: nextOrder,
    source: '',
    sourcePages: '',
    tags: '',
    images: [],
    subquestions: [buildEmptySubquestion(1)],
  }
}

function stationToDraft(station: AdminStation): StationDraft {
  return {
    id: station.id,
    sectionId: station.sectionId,
    title: station.title,
    scenario: station.scenario,
    type: STATION_TYPES.includes(station.type as StationType) ? (station.type as StationType) : 'clinical',
    status: station.status,
    sortOrder: station.sortOrder,
    source: station.source ?? '',
    sourcePages: Array.isArray(station.sourcePages)
      ? station.sourcePages.join('\n')
      : (station.sourcePages ?? ''),
    tags: station.tags.join(', '),
    images: station.images?.map(mapMediaToDraft) ?? [],
    subquestions: station.subquestions?.map(mapSubquestion) ?? [],
  }
}

function normalizePositiveInteger(value: string, fallback: number): number {
  const parsed = Number(value)
  if (!Number.isInteger(parsed) || parsed < 0) return fallback
  return parsed
}

function deriveSourcePages(input: string): string | string[] | null {
  const normalized = input.trim()
  if (!normalized) return null
  return normalized
    .split('\n')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .filter(Boolean)
}

function getErrorMessage(error: unknown): string {
  if (error instanceof AdminApiError) {
    if (error.status === 401) return 'انتهت صلاحية جلسة الدخول. أعد تسجيل الدخول.'
    if (error.status === 403) return 'هذا الحساب غير مصرح له بإدارة المحتوى.'
    return error.message || 'تعذر تنفيذ الطلب.'
  }
  if (error instanceof Error) return error.message
  return 'تعذر تنفيذ الطلب.'
}

function reorderItems<T extends { sortOrder: number }>(items: T[]): T[] {
  return items.map((item, index) => ({
    ...item,
    sortOrder: index + 1,
  }))
}

export function AdminCMSPage({ getAuthToken }: AdminCMSPageProps) {
  const session = useAuthSession()
  const authTokenProvider = getAuthToken ?? getFirebaseIdToken

  const [api] = useState<AdminApi>(() => createAdminApiClient({ getToken: authTokenProvider }))
  const [sections, setSections] = useState<Section[]>([])
  const [stations, setStations] = useState<AdminStationSummary[]>([])
  const [stationListMeta, setStationListMeta] = useState<StationListState>({
    total: 0,
    page: 1,
    pageSize: 24,
    hasMore: false,
  })
  const [loading, setLoading] = useState(true)
  const [mainError, setMainError] = useState<string | null>(null)

  const [searchText, setSearchText] = useState('')
  const [sectionFilter, setSectionFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState<StationStatus | 'all'>('all')
  const [anomalyOnly, setAnomalyOnly] = useState(false)
  const [includeArchived, setIncludeArchived] = useState(false)

  const [mode, setMode] = useState<EditorMode>('idle')
  const [draft, setDraft] = useState<StationDraft | null>(null)
  const [originalStation, setOriginalStation] = useState<AdminStation | null>(null)
  const [modeError, setModeError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const [previewMode, setPreviewMode] = useState(false)
  const [previewReveals, setPreviewReveals] = useState<Record<string, boolean>>({})

  const [mediaUploadBusy, setMediaUploadBusy] = useState(false)
  const [mediaAltText, setMediaAltText] = useState('')
  const [mediaSortOrder, setMediaSortOrder] = useState('')
  const deepLinkHandled = useRef(false)

  const sectionMap = useMemo(() => {
    const map = new Map<string, Section>()
    for (const section of sections) {
      map.set(section.id, section)
    }
    return map
  }, [sections])

  const sectionNames = useMemo(
    () => {
      return [
        { value: 'all', label: 'كل الأقسام' },
        ...sections.map((section) => ({ value: section.id, label: section.name })),
      ]
    },
    [sections],
  )

  const canUseAdmin = !session.loading && session.isAdmin

  const loadSections = useCallback(async () => {
    try {
      const response = await api.listSections()
      setSections(response.sections)
    } catch (error) {
      throw error
    }
  }, [api])

  const loadStations = useCallback(async () => {
    try {
      const response = await api.listStations({
        search: searchText.trim() || undefined,
        sectionId: sectionFilter,
        status: statusFilter,
        includeArchived: includeArchived || statusFilter === 'archived',
        page: stationListMeta.page,
        pageSize: stationListMeta.pageSize,
      })
      setStations(response.stations)
      setStationListMeta({
        total: response.total,
        page: response.page,
        pageSize: response.pageSize,
        hasMore: response.hasMore,
      })
    } catch (error) {
      throw error
    }
  }, [api, includeArchived, searchText, sectionFilter, statusFilter, stationListMeta.page, stationListMeta.pageSize])

  const refreshData = useCallback(async () => {
    setLoading(true)
    setMainError(null)
    try {
      await loadSections()
      await loadStations()
    } catch (error) {
      setMainError(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }, [loadSections, loadStations])

  useEffect(() => {
    if (!canUseAdmin) {
      setLoading(false)
      return
    }
    void refreshData()
  }, [canUseAdmin, refreshData])

  useEffect(() => {
    setStationListMeta((previous) => ({ ...previous, page: 1 }))
  }, [searchText, sectionFilter, statusFilter, anomalyOnly, includeArchived])

  const visibleStations = useMemo(() => {
    const normalizedAnomalies = sections.length ? new Set(sections.map((section) => section.id)) : new Set<string>()
    const filtered = stations
      .filter((station) => {
        if (anomalyOnly) {
          return normalizeStationAnomaly(station, normalizedAnomalies)
        }
        return true
      })
      .sort((left, right) => left.sortOrder - right.sortOrder || left.title.localeCompare(right.title, 'ar'))
    return filtered
  }, [anomalyOnly, stations, sections])

  const hasSessionError = !canUseAdmin && !session.loading

  const clearMode = useCallback(() => {
    setMode('idle')
    setDraft(null)
    setOriginalStation(null)
    setModeError(null)
    setPreviewMode(false)
    setPreviewReveals({})
  }, [])

  const updateDraft = useCallback((updater: (next: StationDraft) => StationDraft) => {
    setDraft((previous) => {
      if (!previous) return previous
      const next = updater(previous)
      return next
    })
  }, [])

  const normalizeDraftFromStation = useCallback(async (stationId: string) => {
    const response = await api.getStation(stationId)
    setOriginalStation(response)
    setDraft(stationToDraft(response))
    setMode('edit')
    setModeError(null)
    setPreviewMode(false)
    setPreviewReveals({})
  }, [api])

  const startCreate = useCallback(() => {
    const draftValue = createBlankDraft(sections, stations)
    setMode('create')
    setOriginalStation(null)
    setDraft(draftValue)
    setModeError(null)
    setPreviewMode(false)
    setPreviewReveals({})
  }, [sections, stations])

  const startEdit = useCallback(async (stationId: string) => {
    setModeError(null)
    setMode('edit')
    setDraft(null)
    try {
      await normalizeDraftFromStation(stationId)
    } catch (error) {
      setMode('idle')
      setModeError(getErrorMessage(error))
    }
  }, [normalizeDraftFromStation])

  const archiveStation = useCallback(async (stationId: string) => {
    if (!window.confirm('هل تريد أرشفة هذه المحطة؟ ستختفي من صفحات الطلاب، ويمكن استعادتها لاحقًا.')) return
    try {
      await api.archiveStation(stationId)
      await refreshData()
      if (draft?.id === stationId) {
        clearMode()
      }
    } catch (error) {
      setMainError(getErrorMessage(error))
    }
  }, [api, clearMode, draft?.id, refreshData])

  const reorderStation = useCallback(async (stationId: string, direction: -1 | 1) => {
    if (stations.length <= 1) return
    const index = visibleStations.findIndex((station) => station.id === stationId)
    if (index < 0) return

    const nextIndex = index + direction
    if (nextIndex < 0 || nextIndex >= visibleStations.length) return
    const current = visibleStations[index]
    const swap = visibleStations[nextIndex]
    if (!current || !swap) return

    try {
      await api.updateStation(current.id, { sortOrder: swap.sortOrder })
      await api.updateStation(swap.id, { sortOrder: current.sortOrder })
      await refreshData()
    } catch (error) {
      setMainError(getErrorMessage(error))
    }
  }, [api, visibleStations, stations.length, refreshData])

  const addSubquestion = useCallback(() => {
    updateDraft((previous) => ({
      ...previous,
      subquestions: reorderItems([...previous.subquestions, buildEmptySubquestion(previous.subquestions.length + 1)]),
    }))
  }, [updateDraft])

  useEffect(() => {
    if (!canUseAdmin || deepLinkHandled.current || typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const stationId = params.get('station')?.trim()
    if (!stationId) return

    deepLinkHandled.current = true
    void startEdit(stationId).then(() => {
      if (params.get('addQuestion') === '1') addSubquestion()
    })
  }, [addSubquestion, canUseAdmin, startEdit])

  const removeSubquestion = useCallback((index: number) => {
    updateDraft((previous) => {
      const next = previous.subquestions.filter((_entry, current) => current !== index)
      return {
        ...previous,
        subquestions: reorderItems(next),
      }
    })
  }, [updateDraft])

  const moveSubquestion = useCallback((index: number, direction: -1 | 1) => {
    updateDraft((previous) => {
      const next = [...previous.subquestions]
      const target = index + direction
      if (target < 0 || target >= next.length) return previous
      const [entry] = next.splice(index, 1)
      if (!entry) return previous
      next.splice(target, 0, entry)
      return { ...previous, subquestions: reorderItems(next) }
    })
  }, [updateDraft])

  const setSubquestionField = useCallback((index: number, field: keyof DraftSubquestion, value: string) => {
    updateDraft((previous) => {
      const next = [...previous.subquestions]
      const current = next[index]
      if (!current) return previous
       const copy = { ...current, [field]: value } as DraftSubquestion
       if (field === 'sortOrder') copy.sortOrder = normalizePositiveInteger(value, current.sortOrder)
       if (field === 'prompt') copy.prompt = value
      next[index] = copy
      return { ...previous, subquestions: next }
    })
  }, [updateDraft])

  const addAnswer = useCallback((subIndex: number) => {
    updateDraft((previous) => {
      const next = [...previous.subquestions]
      const question = next[subIndex]
      if (!question) return previous
      const nextQuestion = {
        ...question,
        answers: reorderItems([...question.answers, buildEmptyAnswer()]),
      }
      next[subIndex] = nextQuestion
      return { ...previous, subquestions: next }
    })
  }, [updateDraft])

  const removeAnswer = useCallback((subIndex: number, answerIndex: number) => {
    updateDraft((previous) => {
      const next = [...previous.subquestions]
      const question = next[subIndex]
      if (!question) return previous
      const nextAnswers = question.answers.filter((_answer, index) => index !== answerIndex)
      question.answers = reorderItems(nextAnswers)
      next[subIndex] = question
      return { ...previous, subquestions: next }
    })
  }, [updateDraft])

  const moveAnswer = useCallback((subIndex: number, answerIndex: number, direction: -1 | 1) => {
    updateDraft((previous) => {
      const next = [...previous.subquestions]
      const question = next[subIndex]
      if (!question) return previous

      const answers = [...question.answers]
      const target = answerIndex + direction
      if (target < 0 || target >= answers.length) return previous
      const [answer] = answers.splice(answerIndex, 1)
      if (!answer) return previous
      answers.splice(target, 0, answer)
      question.answers = reorderItems(answers)
      next[subIndex] = question
      return { ...previous, subquestions: next }
    })
  }, [updateDraft])

  const setAnswerField = useCallback((subIndex: number, answerIndex: number, field: keyof DraftAnswer, value: string | boolean) => {
    updateDraft((previous) => {
      const next = [...previous.subquestions]
      const question = next[subIndex]
      if (!question) return previous
      const answers = [...question.answers]
      const answer = answers[answerIndex]
      if (!answer) return previous
       answers[answerIndex] = {
         ...answer,
         [field]: field === 'isPrimary'
           ? Boolean(value)
           : field === 'sortOrder'
             ? normalizePositiveInteger(String(value), answer.sortOrder)
             : String(value),
       }
      question.answers = answers
      return { ...previous, subquestions: next }
    })
  }, [updateDraft])

  const setMainDraftField = useCallback(<K extends keyof StationDraft>(field: K, value: StationDraft[K]) => {
    updateDraft((previous) => ({ ...previous, [field]: value }))
  }, [updateDraft])

  const prepareCreatePayload = (target: StationDraft): CreateStationInput => {
    return {
      ...buildStationDraftPayload(target),
      source: target.source.trim(),
      sourcePages: deriveSourcePages(target.sourcePages),
      subquestions: target.subquestions
        .filter((question) => question.prompt.trim().length > 0)
        .map((question): CreateSubquestionInput => ({
          stationId: '',
          prompt: question.prompt.trim(),
          questionType: question.questionType || 'text',
          sortOrder: question.sortOrder,
          marks: question.marks ? normalizeDraftToNumber(question.marks) : null,
          answers: question.answers
            .filter((answer) => answer.body.trim().length > 0)
            .map((answer) => ({
              subquestionId: '',
              content: answer.body.trim(),
              sortOrder: answer.sortOrder,
              isPrimary: answer.isPrimary,
            })),
        })),
      tags: parseTagsFromInput(target.tags),
      type: target.type,
      status: target.status,
    }
  }

  const prepareUpdatePayload = (target: StationDraft): UpdateStationInput => {
    return {
      sectionId: target.sectionId,
      title: target.title.trim(),
      scenario: target.scenario.trim(),
      type: target.type,
      source: target.source.trim(),
      sourcePages: deriveSourcePages(target.sourcePages),
      sortOrder: target.sortOrder,
      status: target.status,
      tags: parseTagsFromInput(target.tags),
    }
  }

  const syncAnswers = useCallback(async (subquestionId: string, sourceAnswers: DraftAnswer[], targetAnswers: AdminAnswer[]) => {
    const sourceIds = new Set<string>()

    for (const answer of sourceAnswers) {
      const normalizedBody = answer.body.trim()
      if (!normalizedBody) continue

      const payload: UpdateAnswerInput = {
        content: normalizedBody,
        sortOrder: answer.sortOrder,
        isPrimary: answer.isPrimary,
      }
      if (answer.id) {
        sourceIds.add(answer.id)
        await api.updateAnswer(answer.id, payload)
      } else {
        await api.createAnswer({
          content: normalizedBody,
          sortOrder: answer.sortOrder,
          isPrimary: answer.isPrimary,
          subquestionId,
        })
      }
    }

    for (const target of targetAnswers) {
      const isKept = sourceIds.has(target.id)
      if (!isKept) {
        await api.deleteAnswer(target.id)
      }
    }
  }, [api])

  const syncSubquestions = useCallback(async (stationId: string, draftSubquestions: DraftSubquestion[], sourceSubquestions: AdminStation['subquestions']) => {
    const sourceIds = new Set<string>()
    const sourceById = new Map<string, AdminStation['subquestions'][number]>()
    for (const item of sourceSubquestions) {
      sourceById.set(item.id, item)
    }

    for (const subquestion of draftSubquestions) {
      const prompt = subquestion.prompt.trim()
      if (!prompt) continue

      const payload: UpdateSubquestionInput = {
        prompt,
        questionType: subquestion.questionType || 'text',
        sortOrder: subquestion.sortOrder,
        marks: subquestion.marks ? normalizeDraftToNumber(subquestion.marks) : null,
      }

      if (subquestion.id) {
        const source = sourceById.get(subquestion.id)
        sourceIds.add(subquestion.id)
        if (source) {
          await api.updateSubquestion(subquestion.id, payload)
          await syncAnswers(subquestion.id, subquestion.answers, source.answers)
        }
      } else {
        const created = await api.createSubquestion({
          stationId,
          prompt,
          questionType: subquestion.questionType || 'text',
          sortOrder: subquestion.sortOrder,
          marks: subquestion.marks ? normalizeDraftToNumber(subquestion.marks) : null,
          answers: [],
        })
        if (created.id) {
          for (const answer of subquestion.answers) {
            const normalized = answer.body.trim()
            if (!normalized) continue
            await api.createAnswer({
              subquestionId: created.id,
              content: normalized,
              sortOrder: answer.sortOrder,
              isPrimary: answer.isPrimary,
            })
          }
        }
      }
    }

    for (const source of sourceSubquestions) {
      if (!sourceIds.has(source.id)) {
        await api.deleteSubquestion(source.id)
      }
    }
  }, [api, syncAnswers])

  const saveDraft = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!draft) return

    if (!draft.sectionId) {
      setModeError('اختر القسم قبل الحفظ.')
      return
    }
    if (!draft.title.trim()) {
      setModeError('اكتب عنوانًا للمحطة.')
      return
    }

    setSaving(true)
    setModeError(null)

    try {
      if (mode === 'create') {
        const created = await api.createStation(prepareCreatePayload(draft))
        await normalizeDraftFromStation(created.id)
        setMode('edit')
        setModeError('تم إنشاء المحطة بنجاح.')
      } else if (mode === 'edit' && draft.id && originalStation) {
        await api.updateStation(draft.id, prepareUpdatePayload(draft))
        await syncSubquestions(draft.id, draft.subquestions, originalStation.subquestions || [])
        await normalizeDraftFromStation(draft.id)
        setModeError('تم حفظ التعديلات بنجاح.')
      } else {
        throw new Error('لا توجد محطة مفتوحة للتحرير حاليًا.')
      }
      await refreshData()
    } catch (error) {
      setModeError(getErrorMessage(error))
    } finally {
      setSaving(false)
    }
  }, [api, draft, mode, originalStation, normalizeDraftFromStation, refreshData, syncSubquestions])

  const togglePreviewQuestionAnswer = useCallback((questionId: string) => {
    setPreviewReveals((previous) => ({
      ...previous,
      [questionId]: !previous[questionId],
    }))
  }, [])

  const toggleAllAnswers = useCallback((visible: boolean) => {
    if (!draft) return
    const next: Record<string, boolean> = {}
    for (const question of draft.subquestions) {
      const key = question.id || question.localId
      next[key] = visible
    }
    setPreviewReveals(next)
  }, [draft])

  const mediaUpload = useCallback(async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    if (!draft?.id) {
      setModeError('احفظ المحطة أولا قبل رفع أي صورة.')
      return
    }
    const fileInput = event.currentTarget
      .closest('.admin-media-form')
      ?.querySelector('input[type="file"]') as HTMLInputElement | null
    const file = fileInput?.files?.[0]
    if (!file) {
      setModeError('اختر ملف الصورة أولاً.')
      return
    }

    setMediaUploadBusy(true)
    try {
      const uploaded = await api.uploadMedia(draft.id, file, {
        altText: mediaAltText.trim() || undefined,
        sortOrder: mediaSortOrder ? normalizePositiveInteger(mediaSortOrder, draft.images.length + 1) : draft.images.length + 1,
      })
      setDraft((previous) => {
        if (!previous) return previous
        return {
          ...previous,
          images: reorderItems([...previous.images, mapMediaToDraft(uploaded)]),
        }
      })
      setModeError('تم رفع الصورة بنجاح.')
      setMediaAltText('')
      setMediaSortOrder('')
      if (fileInput) {
        fileInput.value = ''
      }
    } catch (error) {
      setModeError(getErrorMessage(error))
    } finally {
      setMediaUploadBusy(false)
    }
  }, [api, draft?.id, draft?.images.length, mediaAltText, mediaSortOrder])

  const removeMedia = useCallback(async (mediaKey: string) => {
    if (!draft?.id) return
    if (!window.confirm('هل تريد حذف الصورة؟')) return
    try {
      await api.deleteMedia(mediaKey)
      setDraft((previous) => {
        if (!previous) return previous
        return {
          ...previous,
          images: previous.images.filter((media) => media.key !== mediaKey),
        }
      })
      setModeError('تم حذف الصورة.')
    } catch (error) {
      setModeError(getErrorMessage(error))
    }
  }, [api, draft?.id])

  if (session.loading) {
    return (
      <main className="admin-cms" dir="rtl" aria-live="polite">
        <p className="admin-status">جارٍ التحقق من صلاحيات الحساب…</p>
      </main>
    )
  }

  if (hasSessionError) {
    return (
      <main className="admin-cms" dir="rtl">
        <section className="admin-card admin-card--warning">
          <h1>واجهة الإدارة غير متاحة</h1>
          <p>الحساب الحالي غير مصرح له بإدارة المحتوى.</p>
        </section>
      </main>
    )
  }

  return (
    <main className="admin-cms" dir="rtl" aria-label="إدارة محتوى المحطات">
      <header className="admin-header">
        <div>
          <p className="admin-kicker">إدارة المحتوى</p>
          <h1>إدارة محطات العملي</h1>
          <p className="admin-subtitle">ابحث عن المحطات وعدّل أسئلتها وصورها وترتيب ظهورها.</p>
        </div>
        <button type="button" className="admin-refresh" onClick={() => void refreshData()}>
          <RefreshCw size={16} />
          تحديث
        </button>
      </header>

      <section className="admin-filters">
        <label className="admin-field">
          <span>بحث</span>
          <span className="admin-field-inline">
            <Search size={16} aria-hidden="true" />
            <input
              type="search"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder="ابحث بالعنوان أو السيناريو أو الوسم…"
              aria-label="بحث عن محطة"
            />
          </span>
        </label>

        <label className="admin-field">
          <span>القسم</span>
          <select
            value={sectionFilter}
            onChange={(event) => setSectionFilter(event.target.value)}
            aria-label="تصفية حسب القسم"
          >
            {sectionNames.map((section) => (
              <option key={section.value} value={section.value}>
                {section.label}
              </option>
            ))}
          </select>
        </label>

        <label className="admin-field">
          <span>الحالة</span>
          <select
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value as StationStatus | 'all')
            }}
            aria-label="تصفية حسب الحالة"
          >
            <option value="all">كل الحالات</option>
            <option value="draft">مسودة</option>
            <option value="needs_review">بانتظار المراجعة</option>
            <option value="published">منشورة</option>
            <option value="archived">مؤرشفة</option>
          </select>
        </label>

        <label className="admin-checkbox">
          <input
            type="checkbox"
            checked={anomalyOnly}
            onChange={(event) => setAnomalyOnly(event.target.checked)}
          />
           إظهار المحطات التي بها ملاحظات فقط
        </label>

        <label className="admin-checkbox">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(event) => setIncludeArchived(event.target.checked)}
          />
          إظهار المؤرشفة
        </label>

        <button type="button" className="admin-button" onClick={startCreate}>
          <Plus size={16} aria-hidden="true" />
          إنشاء محطة جديدة
        </button>
      </section>

      {mainError && (
        <p className="admin-error" role="alert">
          {mainError}
        </p>
      )}

      <section className="admin-board">
        <section className="admin-panel">
          <header className="admin-panel-header">
            <h2>المحطات ({stationListMeta.total})</h2>
            <p>الصفحة {stationListMeta.page} / {Math.max(1, Math.ceil(stationListMeta.total / stationListMeta.pageSize))}</p>
          </header>

          {loading && <p className="admin-status">جارٍ تحميل المحطات…</p>}

          {!loading && visibleStations.length === 0 && (
            <p className="admin-empty">لا توجد محطات تطابق عوامل التصفية الحالية.</p>
          )}

          <ul className="admin-list" aria-label="قائمة المحطات">
            {visibleStations.map((station) => {
              const hasAnomaly = normalizeStationAnomaly(
                station,
                new Set(sections.map((section) => section.id)),
              )
              return (
                <li key={station.id} className={`admin-item ${hasAnomaly ? 'admin-item--anomaly' : ''}`}>
                  <div className="admin-item-header">
                    <div>
                      <h3>{station.title}</h3>
                      <p>{sectionMap.get(station.sectionId)?.name || `قسم غير موجود (${station.sectionId})`}</p>
                    </div>
                     <p className="admin-badge">{formatStatusLabel(station.status)}</p>
                  </div>
                  <p className="admin-meta">
                    ترتيب:
                    {' '}
                    {station.sortOrder}
                    {' '}
                    · أسئلة:
                    {' '}
                    {station.questionCount ?? 0}
                    {' '}
                    · صور:
                    {' '}
                    {station.mediaCount ?? 0}
                    {' '}
                    · وسوم:
                    {' '}
                    {(station.tags || []).length}
                  </p>

                  <div className="admin-actions">
                    <button type="button" onClick={() => startEdit(station.id)} title="تعديل">
                      <Pencil size={15} aria-hidden="true" />
                      تعديل
                    </button>
                    <button
                      type="button"
                      onClick={() => void reorderStation(station.id, -1)}
                      aria-label={`نقل ${station.title} للأعلى`}
                    >
                      <ArrowUp size={15} aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => void reorderStation(station.id, 1)}
                      aria-label={`نقل ${station.title} للأسفل`}
                    >
                      <ArrowDown size={15} aria-hidden="true" />
                    </button>
                    <button type="button" onClick={() => archiveStation(station.id)}>
                      <Archive size={15} aria-hidden="true" />
                      أرشفة
                    </button>
                  </div>
                  {hasAnomaly && (
                    <p className="admin-anomaly-tag" role="status">بيانات هذه المحطة غير مكتملة. راجع القسم والسيناريو والأسئلة والصور.</p>
                  )}
                </li>
              )
            })}
          </ul>

          <div className="admin-pagination">
            <button
              type="button"
              disabled={stationListMeta.page <= 1}
              onClick={() => setStationListMeta((previous) => ({ ...previous, page: previous.page - 1 }))}
            >
              السابقة
            </button>
            <button
              type="button"
              disabled={!stationListMeta.hasMore}
              onClick={() => setStationListMeta((previous) => ({ ...previous, page: previous.page + 1 }))}
            >
              التالية
            </button>
          </div>
        </section>

        <section className="admin-editor">
          {mode === 'idle' ? (
            <div className="admin-card">
              <h2>ابدأ التحرير</h2>
              <p>اختر محطة من القائمة أو أنشئ محطة جديدة لبدء التحرير.</p>
            </div>
          ) : (
            <form className="admin-editor-form" onSubmit={saveDraft}>
              <header className="admin-editor-header">
                <div>
                  <h2>{mode === 'create' ? 'إنشاء محطة' : `تعديل: ${draft?.title || 'محطة جديدة'}`}</h2>
                  <p className="admin-subtitle">
                     عدّل بيانات المحطة وأسئلتها وإجاباتها وصورها من هنا.
                  </p>
                </div>
                <button type="button" className="admin-button admin-button--ghost" onClick={clearMode}>
                  إغلاق المحرر
                </button>
              </header>

              {modeError && <p className="admin-error" role="status">{modeError}</p>}

              <div className="admin-grid-2">
                <label className="admin-field">
                  <span>القسم</span>
                  <select
                    value={draft?.sectionId ?? ''}
                    onChange={(event) => setMainDraftField('sectionId', event.target.value)}
                  >
                    <option value="" disabled>اختر القسم</option>
                     {sections.map((section) => (
                       <option key={section.id} value={section.id}>
                         {section.name}
                       </option>
                     ))}
                  </select>
                </label>
                <label className="admin-field">
                  <span>نوع المحطة</span>
                  <select
                    value={draft?.type ?? 'clinical'}
                    onChange={(event) => setMainDraftField('type', event.target.value as StationType)}
                  >
                    {STATION_TYPES.map((type) => (
                      <option key={type} value={type}>{STATION_TYPE_LABELS[type]}</option>
                    ))}
                  </select>
                </label>
                <label className="admin-field">
                  <span>الحالة</span>
                  <select
                    value={draft?.status ?? DEFAULT_STATUS}
                    onChange={(event) => setMainDraftField('status', event.target.value as StationStatus)}
                  >
                    <option value="draft">مسودة</option>
                    <option value="needs_review">بانتظار المراجعة</option>
                    <option value="published">منشورة</option>
                    <option value="archived">مؤرشفة</option>
                  </select>
                </label>
                <label className="admin-field">
                  <span>الترتيب</span>
                  <input
                    type="number"
                    value={draft?.sortOrder ?? 1}
                    min={1}
                    onChange={(event) => {
                      setMainDraftField('sortOrder', normalizePositiveInteger(event.target.value, 1))
                    }}
                  />
                </label>
                <label className="admin-field admin-field--full">
                  <span>عنوان المحطة</span>
                  <input
                    type="text"
                    value={draft?.title ?? ''}
                    onChange={(event) => setMainDraftField('title', event.target.value)}
                    placeholder="اكتب عنوانًا واضحًا للمحطة"
                  />
                </label>
                <label className="admin-field admin-field--full">
                  <span>المصدر</span>
                  <input
                    type="text"
                    value={draft?.source ?? ''}
                    onChange={(event) => setMainDraftField('source', event.target.value)}
                  />
                </label>
                <label className="admin-field admin-field--full">
                  <span>روابط أو صفحات المصدر</span>
                  <textarea
                    rows={2}
                    value={draft?.sourcePages ?? ''}
                    onChange={(event) => setMainDraftField('sourcePages', event.target.value)}
                    placeholder="أدخل كل رابط أو رقم صفحة في سطر منفصل"
                  />
                </label>
                <label className="admin-field admin-field--full">
                  <span>الوسوم (افصل بينها بفاصلة)</span>
                  <input
                    type="text"
                    value={draft?.tags ?? ''}
                    onChange={(event) => setMainDraftField('tags', event.target.value)}
                    placeholder="مثال: تشخيص، سريري، جراحة"
                  />
                </label>
                <label className="admin-field admin-field--full">
                  <span>السيناريو</span>
                  <textarea
                    rows={6}
                    value={draft?.scenario ?? ''}
                    onChange={(event) => setMainDraftField('scenario', event.target.value)}
                    placeholder="اكتب نص السيناريو الرئيسي للمحطة"
                  />
                </label>
              </div>

              <section className="admin-subsection">
                <div className="admin-subsection-header">
                  <h3>الأسئلة الفرعية</h3>
                  <button type="button" onClick={addSubquestion}>
                    <Plus size={14} />
                    سؤال جديد
                  </button>
                </div>

                {draft?.subquestions.length === 0 ? (
                  <p>لا توجد أسئلة مضافة بعد.</p>
                ) : (
                  <ol className="admin-subquestion-list">
                    {draft?.subquestions.map((question, questionIndex) => (
                      <li key={question.id ?? question.localId} className="admin-subquestion-card">
                        <div className="admin-subquestion-header">
                          <label className="admin-field">
                            <span>نص السؤال</span>
                            <textarea
                              rows={2}
                              value={question.prompt}
                              onChange={(event) => setSubquestionField(questionIndex, 'prompt', event.target.value)}
                              placeholder="اكتب السؤال الفرعي"
                            />
                          </label>
                          <div className="admin-small-actions">
                            <button type="button" onClick={() => moveSubquestion(questionIndex, -1)}>
                              <ArrowUp size={14} />
                            </button>
                            <button type="button" onClick={() => moveSubquestion(questionIndex, 1)}>
                              <ArrowDown size={14} />
                            </button>
                            <button type="button" onClick={() => removeSubquestion(questionIndex)}>
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                        <div className="admin-grid-2">
                          <label className="admin-field">
                            <span>نوع السؤال</span>
                            <input
                              type="text"
                              value={question.questionType}
                              onChange={(event) => setSubquestionField(questionIndex, 'questionType', event.target.value)}
                              placeholder="مثال: سؤال نصي"
                            />
                          </label>
                          <label className="admin-field">
                            <span>ترتيب</span>
                            <input
                              type="number"
                              min={1}
                              value={question.sortOrder}
                              onChange={(event) => setSubquestionField(questionIndex, 'sortOrder', event.target.value)}
                            />
                          </label>
                          <label className="admin-field">
                            <span>الدرجة</span>
                            <input
                              type="number"
                              min={0}
                              step={1}
                              value={question.marks}
                              onChange={(event) => setSubquestionField(questionIndex, 'marks', event.target.value)}
                              placeholder="0"
                            />
                          </label>
                        </div>

                        <div className="admin-answer-header">
                          <h4>الإجابات</h4>
                          <button type="button" onClick={() => addAnswer(questionIndex)}>
                            <Plus size={14} />
                            إضافة إجابة
                          </button>
                        </div>

                        <ul className="admin-answer-list">
                          {question.answers.map((answer, answerIndex) => (
                            <li key={answer.id ?? answer.localId} className="admin-answer-row">
                              <label className="admin-field admin-field--full">
                                <span>نص الإجابة</span>
                                <input
                                  type="text"
                                  value={answer.body}
                                  onChange={(event) => setAnswerField(questionIndex, answerIndex, 'body', event.target.value)}
                                  placeholder="إجابة"
                                />
                              </label>
                              <label className="admin-field">
                                <span>ترتيب</span>
                                <input
                                  type="number"
                                  value={answer.sortOrder}
                                  min={1}
                                  onChange={(event) => setAnswerField(
                                    questionIndex,
                                    answerIndex,
                                    'sortOrder',
                                    event.target.value,
                                  )}
                                />
                              </label>
                              <label className="admin-checkbox">
                                <input
                                  type="checkbox"
                                  checked={answer.isPrimary}
                                  onChange={(event) => setAnswerField(
                                    questionIndex,
                                    answerIndex,
                                    'isPrimary',
                                    event.target.checked,
                                  )}
                                />
                                إجابة أساسية
                              </label>
                              <div className="admin-small-actions">
                                <button type="button" onClick={() => moveAnswer(questionIndex, answerIndex, -1)}>
                                  <ArrowUp size={14} />
                                </button>
                                <button type="button" onClick={() => moveAnswer(questionIndex, answerIndex, 1)}>
                                  <ArrowDown size={14} />
                                </button>
                                <button type="button" onClick={() => removeAnswer(questionIndex, answerIndex)}>
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ol>
                )}
              </section>

              <section className="admin-subsection">
                <div className="admin-subsection-header">
                  <h3>صور المحطة</h3>
                  <button
                    type="button"
                    className="admin-small-button"
                    onClick={() => {
                      setPreviewMode((current) => !current)
                    }}
                  >
                    <Eye size={14} aria-hidden="true" />
                    {previewMode ? 'إخفاء المعاينة' : 'معاينة كطالب'}
                  </button>
                </div>

                {!draft?.id ? (
                  <p className="admin-subtext">يمكنك رفع الصور بعد حفظ المحطة أولًا.</p>
                ) : (
                  <div className="admin-media-form">
                    <label className="admin-field">
                      <span>ملف الصورة</span>
                      <input type="file" accept="image/*" />
                    </label>
                    <label className="admin-field">
                      <span>وصف الصورة</span>
                      <input
                        type="text"
                        value={mediaAltText}
                        onChange={(event) => setMediaAltText(event.target.value)}
                        placeholder="صف محتوى الصورة باختصار"
                      />
                    </label>
                    <label className="admin-field">
                      <span>ترتيب</span>
                      <input
                        type="number"
                        min={1}
                        value={mediaSortOrder}
                        onChange={(event) => setMediaSortOrder(event.target.value)}
                        placeholder="ترتيب الصورة"
                      />
                    </label>
                    <button type="button" disabled={mediaUploadBusy} onClick={(event) => void mediaUpload(event)}>
                      <Upload size={14} aria-hidden="true" />
                      {mediaUploadBusy ? 'جارٍ رفع الصورة…' : 'رفع صورة'}
                    </button>
                  </div>
                )}

                <ul className="admin-media-list">
                  {draft?.images.map((media) => (
                    <li key={media.key} className="admin-media-item">
                      <img src={media.url} alt={media.altText || 'صورة محطّة'} />
                      <div>
                        <p>{media.filename || 'بدون اسم ملف'}</p>
                        <p>ترتيب: {media.sortOrder}</p>
                      </div>
                      <button type="button" onClick={() => void removeMedia(media.key)}>
                        <Trash2 size={14} aria-hidden="true" />
                        حذف
                      </button>
                    </li>
                  ))}
                  {draft?.images.length === 0 && <li>لا توجد صور مرفوعة.</li>}
                </ul>
              </section>

              {previewMode && draft && (
                <section className="admin-preview">
                  <header className="admin-subsection-header">
                    <h3>معاينة صفحة الطالب</h3>
                    <div className="admin-small-actions">
                      <button type="button" onClick={() => toggleAllAnswers(true)}>
                        إظهار الكل
                      </button>
                      <button type="button" onClick={() => toggleAllAnswers(false)}>
                        إخفاء الكل
                      </button>
                    </div>
                  </header>
                  <article className="admin-preview-body">
                    <header>
                      <p className="admin-preview-title">{draft.title || 'محطة بدون عنوان'}</p>
                      <p>{sectionMap.get(draft.sectionId)?.name || 'قسم غير محدد'}</p>
                    </header>
                    <p>{draft.scenario || 'لا يوجد سيناريو بعد.'}</p>

                    {draft.images.length > 0 && (
                      <div className="admin-preview-media">
                        {draft.images.map((media) => (
                          <figure key={media.key}>
                            <img src={media.url} alt={media.altText || 'صورة محطة'} />
                            <figcaption>{media.altText || 'لا يوجد وصف للصورة'}</figcaption>
                          </figure>
                        ))}
                      </div>
                    )}

                    <ol className="admin-preview-questions">
                      {draft.subquestions.map((question, index) => {
                        const key = question.id || question.localId
                        const revealed = previewReveals[key] ?? false
                        return (
                          <li key={key} className="admin-preview-question">
                            <p>{`${index + 1}- ${question.prompt || 'سؤال فارغ'}`}</p>
                            <button type="button" onClick={() => togglePreviewQuestionAnswer(key)}>
                              {revealed ? 'إخفاء الإجابات' : 'إظهار الإجابات'}
                            </button>
                            {revealed && (
                              <ul>
                                {question.answers.map((answer) => (
                                  <li key={answer.id || answer.localId}>
                                    {answer.body || 'إجابة فارغة'}
                                    {answer.isPrimary && <strong> (صحيحة)</strong>}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </li>
                        )
                      })}
                    </ol>
                  </article>
                </section>
              )}

              <div className="admin-form-actions">
                <button type="submit" disabled={saving}>
                  <Save size={15} aria-hidden="true" />
                  {saving ? 'جارٍ الحفظ…' : 'حفظ التغييرات'}
                </button>
                <button type="button" className="admin-button admin-button--ghost" onClick={clearMode}>
                  إلغاء
                </button>
              </div>
            </form>
          )}
        </section>
      </section>
    </main>
  )
}

export { AdminCMSPage as default }
export { AdminStationToolbar, AdminToolbar, InlineAdminToolbar } from './AdminStationToolbar'

function normalizeDraftToNumber(value: string): number | null {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return null
  return parsed
}
